/**
 * @fileoverview High-resolution glaze photos for the swatch zoom preview.
 *
 * The swatch images in Algolia/Shopify are 400px-wide copies. Their full
 * resolution originals (typically 2400px+) are still served from the legacy
 * `assets.fireclaytile.com` bucket, and each Shopify filename records which
 * original it came from, in one of two forms:
 *
 * - `imgi_<n>_<base64url>.webp` — base64url of an image-handler request
 *   (`{"bucket":"assets.fireclaytile.com","key":"colors/…jpg",…}`), truncated
 *   but always long enough to contain the bucket and key.
 * - `imgi_<n>_<Name>_Single_Color_Detail_Photo.<ext>` — the original's file
 *   name in plain text.
 *
 * Callers must fall back to the Shopify image when the original fails to
 * load: the legacy bucket can go away independently of this code.
 *
 * @module glazeImages
 */

/** Only originals on this host are used. */
export const ORIGINALS_HOST = 'assets.fireclaytile.com';

function decodeBase64Url(s) {
    const usable = s.slice(0, s.length - (s.length % 4));
    try {
        const binary = atob(usable.replace(/-/g, '+').replace(/_/g, '/'));
        return new TextDecoder().decode(Uint8Array.from(binary, (c) => c.charCodeAt(0)));
    } catch {
        return '';
    }
}

/**
 * The full-resolution original for a Shopify glaze swatch URL.
 * @param {string} shopifyUrl - Swatch image URL from Algolia
 * @returns {string|null} The original's URL, or null when it can't be derived
 */
export function highResGlazeUrl(shopifyUrl) {
    const file = /\/imgi_\d+_([^/?#]+)\.\w+(?:[?#]|$)/.exec(String(shopifyUrl || ''))?.[1];
    if (!file) return null;

    const plain = /^([A-Za-z0-9_'-]+_Single_Color_Detail_Photo)$/.exec(file);
    if (plain) return `https://${ORIGINALS_HOST}/colors/${plain[1]}.jpg`;

    const json = decodeBase64Url(file);
    const m = /"bucket":"([^"]+)","key":"([^"]+)"/.exec(json);
    if (!m || m[1] !== ORIGINALS_HOST || !/^[\w./'-]+$/.test(m[2])) return null;
    return `https://${ORIGINALS_HOST}/${m[2]
        .split('/')
        .map((part) => encodeURIComponent(part))
        .join('/')}`;
}

/** url → Promise<{width, height}>; failures stay rejected so they aren't retried */
const sizeCache = new Map();

/**
 * Loads an image once and resolves its natural size. Cached per URL.
 * @param {string} url
 * @returns {Promise<{width: number, height: number}>}
 */
export function loadImageSize(url) {
    if (!sizeCache.has(url)) {
        const promise = new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
            img.onerror = () => reject(new Error(`Failed to load ${url}`));
            img.src = url;
        });
        // Swallow here so an unobserved rejection doesn't surface as an error
        promise.catch(() => {});
        sizeCache.set(url, promise);
    }
    return sizeCache.get(url);
}
